import { fi, enUS } from 'date-fns/esm/locale';

import { OrganizationRoleType, WorkspaceRoleType } from '@robocorp/robocloud-internal-cds-sdk';
import { formatDistanceEn } from './dateFnsLocale';

/**
 * Date used to compare against users last Terms of Service acceptance `details.tos_accepted_ts` timestamp
 */
export const LAST_BREAKING_TOS_CHANGES = new Date('2023-06-30T00:00:00Z').getTime();

export const languages = {
  ENGLISH: 'en',
  FINNISH: 'fi',
};

export const locales: Record<'fi' | 'en', Locale> = {
  fi,
  en: { ...enUS, formatDistance: formatDistanceEn },
};

export const imageStates = {
  READY: 'READY',
  ERROR: 'ERROR',
  BUILDING: 'BUILDING',
};

export const runRealtimeStatusResources = {
  event: 'EVENT',
  console: 'CONSOLE',
};

export const runtimeStates = {
  BUSY: 'BUSY',
  IDLE: 'IDLE',
  OFFLINE: 'OFFLINE',
  RECONNECTING: 'RECONNECTING',
};

export const availabilityStates = {
  DISABLED: 'DISABLED',
  ENABLED: 'ENABLED',
};

export const processRunErrorCodes = {
  RuntimeNotAvailable: 'ERR_RUNTIME_NOT_AVAILABLE',
  NoStageBundle: 'ERR_NO_STAGE_BUNDLE',
  RunCommandRejected: 'ERR_RUN_CMD_REJECTED',
  StartTimeout: 'ERR_START_TIMEOUT',
  RuntimeProvisioningError: 'ERR_RUNTIME_PROVISIONING_FAILED',
  WorkerError: 'ERR_WORKER',
  RuntimeRemoved: 'ERR_RUNTIME_REMOVED',
  ActivityRunTimeout: 'ERR_ACTIVITY_RUN_TIMEOUT',
  ActivityRunStartFailed: 'ERR_ACTIVITY_RUN_START_FAILED',
  RuntimeProvisioningFailed: 'ERR_RUNTIME_PROVISIONING_FAILED',
  FailedToReachRuntime: 'ERR_FAILED_TO_REACH_RUNTIME',
  WorkerDidNotCompleteRun: 'ERR_WORKER_DID_NOT_COMPLETE_RUN',
};

export const activityRunEvents = {
  START: 'START',
  ENV_SETUP_START: 'ENV_SETUP_START',
  ENV_SETUP_END: 'ENV_SETUP_END',
  BUNDLE_DL_START: 'BUNDLE_DL_START',
  BUNDLE_DL_END: 'BUNDLE_DL_END',
  BUNDLE_UNPACK_START: 'BUNDLE_UNPACK_START',
  BUNDLE_UNPACK_END: 'BUNDLE_UNPACK_END',
  BUNDLE_INFO: 'BUNDLE_INFO',
  EXECUTE_START: 'EXECUTE_START',
  EXECUTE_END: 'EXECUTE_END',
  ARTIFACT_UL_START: 'ARTIFACT_UL_START',
  ARTIFACT_UL_END: 'ARTIFACT_UL_END',
  END: 'END',
};

export const webSocketMessageTypes = {
  ProcessRunUpdate: 'processRunUpdate',
  ActivityRunUpdate: 'activityRunUpdate',
  AuthOk: 'AUTH_OK', // authentication complete, websocket active
};

export const webSocketActions = {
  STATE_UPDATE: 'stateUpdate',
  CONSOLE_MESSAGE: 'consoleMessage',
  RUN_EVENT: 'runEvent',
};

export const processRunTriggerTypes = {
  SCHEDULE: 'schedule',
  MANUAL: 'manual',
  API: 'api',
  EMAIL: 'email',
};

export const billingPlanNames: Record<string, string> = {
  dev1: 'Developer',
  team1: 'Team',
  pro1: 'Pro (legacy)',
  free1: 'Free',
  free10: 'Free',
  free20: 'Free',
  basic1: 'Basic',
  pro2: 'Pro',
  pro10: 'Pro',
  pro20: 'Pro',
  starter10: 'Starter',
  starter20: 'Starter',
  flex10: 'Flex',
  flex20: 'Flex',
  enterprise: 'Enterprise',
  'partner-demo10': 'Partner Demo',
  'partner-starter10': 'Partner Starter',
  'partner-pro10': 'Partner Pro',
};

export const validationConstants = {
  requiredField: { required: true },
  email: {
    pattern: '.+@.+\\.\\w.+',
    type: 'email',
    required: true,
    maxLength: 100,
  },
  optionalEmail: {
    pattern: '.+@.+\\.\\w.+',
    type: 'email',
    maxLength: 100,
  },
  emailStrict: {
    pattern: "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$",
    type: 'email',
    required: true,
    maxLength: 100,
  },
  name: {
    required: true,
    maxLength: 50,
  },
  url: {
    maxLength: 300,
  },
  urlWithPattern: {
    maxLength: 300,
    pattern: 'https://*.*|http://*.*',
  },
  zipUrl: {
    pattern: 'https://*.*.zip|http://*.*.zip|https://*.*.ZIP|http://*.*.ZIP',
  },
  organizationShortName: {
    maxLength: 50,
    required: true,
    pattern: '^([a-z0-9]{1,50})(?<!(^(errortest|settings|signin|signup)$))$',
  },
  workspaceShortName: {
    maxLength: 50,
    required: true,
    pattern: '^([a-z0-9]{1,50})(?<!(^(settings)$))$',
  },
  command: {
    maxLength: 1024,
  },
};

export const websocketStatus = {
  0: 'CONNECTING',
  1: 'OPEN',
  2: 'CLOSING',
  3: 'CLOSED',
};

export const ErrorActions = {
  WebhookCreateError: 'CREATE_WEBHOOK_ERROR',
  WebhookEditError: 'EDIT_WEBHOOK_ERROR',
};

export enum ErrorSubCodes {
  ERR_ACTIVITY_ENV_MISSING = 'ERR_ACTIVITY_ENV_MISSING',
  ERR_ACTIVITY_RUN_COMMAND_MISSING = 'ERR_ACTIVITY_RUN_COMMAND_MISSING',
  ERR_NO_ACTIVITIES = 'ERR_NO_ACTIVITIES',
  EMAIL_MISMATCH = 'EMAIL_MISMATCH',
  GIT_REFERENCE_NOT_FOUND_ERROR = 'GIT_REFERENCE_NOT_FOUND_ERROR',
  ERR_TEMPLATE_IN_USE = 'ERR_TEMPLATE_IN_USE',
  TASK_PACKAGE_NOT_FOUND_ERROR = 'TASK_PACKAGE_NOT_FOUND_ERROR',
  ROBOT_WITH_SAME_NAME_ALREADY_EXISTS = 'ROBOT_WITH_SAME_NAME_ALREADY_EXISTS',
  TOO_MANY_WORKSPACES = 'TOO_MANY_WORKSPACES',
  TOO_MANY_PROCESSES = 'TOO_MANY_PROCESSES',
  TOO_MANY_ACTIVITIES = 'TOO_MANY_ACTIVITIES',
  TOO_MANY_USERS_INVITED = 'TOO_MANY_USERS_INVITED',
  TOO_MANY_USERS_IN_WORKSPACE = 'TOO_MANY_USERS_IN_WORKSPACE',
  TOO_MANY_ACCESS_CREDENTIALS = 'TOO_MANY_ACCESS_CREDENTIALS',
  TOO_MANY_ACTIVITY_TEMPLATES = 'TOO_MANY_ACTIVITY_TEMPLATES',
  TOO_MANY_WORKERS_IN_WORKSPACE = 'TOO_MANY_WORKERS_IN_WORKSPACE',
  ERR_WORKSPACE_DISABLED = 'ERR_WORKSPACE_DISABLED',
  ERR_ORG_INSERT_FAILED = 'ERR_ORG_INSERT_FAILED',
  RESOURCE_IN_USE = 'RESOURCE_IN_USE',
  USER_NOT_IN_ORG = 'USER_NOT_IN_ORG',
  USER_ALREADY_IN_WS = 'USER_ALREADY_IN_WS',
  GIT_CLONE_FAILED = 'GIT_CLONE_FAILED',
  INACTIVE_ASSISTANT = 'INACTIVE_ASSISTANT',
  TOO_MANY_ACTIVE_ASSISTANTS = 'TOO_MANY_ACTIVE_ASSISTANTS',
  ERR_TOO_MANY_WORK_ITEMS = 'ERR_TOO_MANY_WORK_ITEMS',
  ERR_NO_WORK_ITEMS = 'ERR_NO_WORK_ITEMS',
  UNAUTHORIZED_TO_LOAD_WORK_ITEMS = 'UNAUTHORIZED_TO_LOAD_WORK_ITEMS',
  ENDPOINT_VALIDATION_FAILED = 'ENDPOINT_VALIDATION_FAILED',
  ERR_CANNOT_REMOVE_LAST_ENV_FROM_ACTIVE_GRP = 'ERR_CANNOT_REMOVE_LAST_ENV_FROM_ACTIVE_GRP',
  ERR_RUNTIME_IN_USE = 'ERR_RUNTIME_IN_USE',
  ERR_ENVIRONMENT_GROUP_IN_USE = 'ERR_ENVIRONMENT_GROUP_IN_USE',
}

export const ExternalLinks = {
  EA_QUESTIONNAIRE: 'https://robocorp.typeform.com/to/LbWRqX',
  TWITTER: 'https://twitter.com/robocorpinc',
  SUPPORT: 'mailto:support@robocorp.com',
  CONTACT_FORM: 'https://sema4.ai/contact-us',
  SALES_CONTACT_FORM: 'https://sema4.ai/contact-us',
  ROBOCORP_PRICING: 'https://sema4.ai/docs/automation/pricing',
  ID: 'https://id.robocorp.com',
  TERMS_OF_USE: 'https://robocorp.com/terms-of-use',
  PRIVACY_POLICY: 'https://sema4.ai/privacy-policy',
  SLACK: 'https://sema4ai-users.slack.com',
  ROBOCORP_ROBOTS: 'https://robocorp.com/robots/',
  EULA: 'https://cdn.robocorp.com/legal/Robocorp-EULA-v1.0.pdf',
  DOWNLOADS: 'https://robocorp.com/download',
  ROBOCORP_PARTNER: 'https://robocorp.com/partners',
  ROBOCORP_PORTAL: 'https://robocorp.com/portal',
  LINK_REMARK: 'https://chat.robocorp.com/',
  SEMA4: 'https://sema4.ai',
};

export const DocsLinks = {
  DOCS: 'https://robocorp.com/docs/',
  CHANGELOG: 'https://updates.robocorp.com/',
  INSTALL_ROBOCORP_SU: 'https://robocorp.com/docs/control-room/setup-utility',
  INSTALL_ROBOCORP_WFA:
    'https://robocorp.com/docs/control-room/unattended/worker-setups/application',
  LINK_ROBOCORP_WFA:
    'https://robocorp.com/docs/control-room/unattended/worker-setups/application#link-robocorp-workforce-agent-with-control-room',
  LINK_ROBOCORP_WFA_GROUPS:
    'https://robocorp.com/docs/control-room/unattended/worker-setups/groups',
  LINK_ROBOCORP_WFA_ON_DEMAND:
    'https://robocorp.com/docs/control-room/unattended/worker-setups/on-demand',
  WORKSPACE_API: 'https://robocorp.com/docs/control-room/apis-and-webhooks',
  API: 'https://robocorp.com/api',
  WEBHOOKS: 'https://robocorp.com/docs/control-room/apis-and-webhooks/webhooks',
  ASSETS_STORAGE: 'https://robocorp.com/docs/control-room/asset-storage',
  PRODUCT_MANUAL: 'https://robocorp.com/docs/control-room',
  ROBOTS: 'https://robocorp.com/docs/control-room/configuring-tasks',
  ROBOT_SETUP: 'https://robocorp.com/docs/robot-structure',
  STEP_CONFIGURATION: 'https://robocorp.com/docs/control-room/configuring-tasks#unattended-process',
  ROBOT_CONFIGURATION: 'https://robocorp.com/docs/control-room/configuring-tasks',
  ASSISTANTS: 'https://robocorp.com/docs/control-room/attended',
  ROBOT_WORKFORCE: 'https://robocorp.com/docs/control-room/unattended',
  WORKERS: 'https://robocorp.com/docs/control-room/unattended#unattended-workers',
  WORKER_GROUPS: 'https://robocorp.com/docs/control-room/unattended/worker-setups/groups',
  WORKERS_ONDEMAND: 'https://robocorp.com/docs/control-room/unattended/worker-setups/on-demand',
  VAULT: 'https://robocorp.com/docs/control-room/vault',
  INSTALL_ROBOCORP_CLI: 'https://robocorp.com/docs/rcc/overview',
  LINK_CERTIFICATION: 'https://robocorp.com/docs/courses',
  LINK_CONTROL_ROOM: 'https://robocorp.com/docs/control-room',
  HOSTED_CONTAINERS:
    'https://robocorp.com/docs/control-room/unattended/worker-setups/robocorp-container',
  LINK_PYTHON_QUICKSTART: 'https://robocorp.com/docs/quickstart-guide/python',
  LINK_VSCODE_EXTENSION: 'https://robocorp.com/docs/visual-studio-code/extension-features',
  TASK_PACKAGE_DOCS: 'https://robocorp.com/docs/python#minimal-example',
  STEP_DOCS: 'https://robocorp.com/docs/control-room/unattended/step-configuration',
  NOTIFICATION_DOCS: 'https://robocorp.com/docs/control-room/unattended/notifications',
  SCHEDULE_DOCS: 'https://robocorp.com/docs/control-room/scheduling-processes',
  ASSET_STORAGE_DOCS: 'https://robocorp.com/docs/control-room/asset-storage',
  VAULT_DOCS: 'https://robocorp.com/docs/control-room/vault',
};

export const ApiLinks = {
  PROCESS_API: `${import.meta.env.VITE_INTEGRATIONS_BASE_URL_V2}/process-v1`,
  ROBOT_API: `${import.meta.env.VITE_INTEGRATIONS_BASE_URL_V2}/robot-v2`,
  ASSISTANT_API: `${import.meta.env.VITE_INTEGRATIONS_BASE_URL}/assistant-v1`,
  LEGACY_EXTERNAL_API: `${import.meta.env.VITE_INTEGRATIONS_BASE_URL}/versions/v1`,
  EXTERNAL_API: `${import.meta.env.VITE_INTEGRATIONS_BASE_URL}/v1`,
};

export const WORK_ITEM_POLL_INTERVAL = 10000;

export const RuntimeType = {
  Standard: 'standard',
  Container: 'container',
};

export const UserMessages = {
  ROLE_CHANGE_SUCCESSFUL: 'ROLE_CHANGE_SUCCESSFUL',
  WORKSPACE_MEMBER_ADD_SUCCESSFUL: 'WORKSPACE_MEMBER_ADD_SUCCESSFUL',
  TEST_EVENT_SENT: 'TEST_EVENT_SENT',
};

export const OrganizationRoleNames: Record<OrganizationRoleType, OrganizationRoleType> = {
  // https://github.com/robocorp/robocloud-logic/blob/master/core-data-service/src/shared/roles/OrganizationRoles.ts
  auditor: 'auditor',
  owner: 'owner',
  admin: 'admin',
  member: 'member',
  data_analyst: 'data_analyst',
  operator: 'operator',
  publisher: 'publisher',
};

export const WorkspaceRoleNames: Record<WorkspaceRoleType, WorkspaceRoleType> = {
  // https://github.com/robocorp/robocloud-logic/blob/master/core-data-service/src/shared/roles/WorkspaceRoles.ts
  auditor: 'auditor',
  controller: 'controller',
  admin: 'admin',
  editor: 'editor',
  member: 'member',
  assistantUser: 'assistantUser',
  customAllyTechnicalManager: 'customAllyTechnicalManager',
  customAllySustain: 'customAllySustain',
  customAllyDeveloper: 'customAllyDeveloper',
  customAllyServiceAccount: 'customAllyServiceAccount',
  customAllyAudit: 'customAllyAudit',
  operator: 'operator',
  knowledgeWorker: 'knowledgeWorker',
  customPmiConfigurator: 'customPmiConfigurator',
  agentSupervisor: 'agentSupervisor',
  customKenvueSupport: 'customKenvueSupport',
};

export const THIRTY_DAYS_IN_MILLISECONDS = 60 * 60 * 24 * 30 * 1000;

export const LOG_ARTIFACT_HTML_FILE_NAME = 'log.html';
export const CONSOLE_ARTIFACT_HTML_FILE_NAME = 'console.txt';

export * from './gitIntegrations';
export * from './chartStyles';

export const CONSUMPTION_PLAN_ID = 'consumption';

export const DEFAULT_WORKER_AUTOREMOVE_MINUTES = 10;
